import { useState, useCallback, useRef, useMemo } from 'react';
import type { GeneratePersonRequest, GeneratePersonResponse } from '../../../../features/admin/actors/api';
import type { WizardData } from '../ActorGenerationWizard';
import { ActorGenerateButton } from '../ActorGenerateButton';
import { downscaleImage } from '../../../../utils/imageUtils';
import { useCreditRates } from '../../../../features/organizations/hooks';
import {
  RESOURCE_TYPE_IMAGE_QUALITY_2K,
  RESOURCE_TYPE_IMAGE_GPT25_FLARE_AUTO_2K,
} from '../../../generation/state/useGenerationState';
import { addToPromptHistory, PROMPT_HISTORY_KEYS } from '../../../../utils/promptHistory';
import { PromptHistoryModal } from '../../../common/PromptHistoryModal';
import { SegmentedTabs } from '../../../common/SegmentedTabs';
import { isCharacterActor } from '../../../../features/actors/actorAttributes';
import styles from './Steps.module.css';
import '../../../../styles/buttons.css';
import '../../../../styles/generation-common.css';
import '../../../../styles/prompt-history.css';

type AttributeNode = {
  id: string;
  key: string;
  label: string;
  type: 'group' | 'option' | 'numeric';
  children?: AttributeNode[];
};

type GeneratePersonFn = (request: GeneratePersonRequest) => Promise<GeneratePersonResponse>;

type Props = {
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
  buildOptions: AttributeNode[];
  appearanceOptions: AttributeNode[];
  generatePersonApi: GeneratePersonFn;
  onGenerateSuccess?: () => void;
  /** 画像生成と並行して名前提案を先読みさせる (待ち時間に隠すため) */
  onPrefetchNames?: () => void;
};

export function PersonGenerationStep({ data, onUpdate, buildOptions, appearanceOptions, generatePersonApi, onGenerateSuccess, onPrefetchNames }: Props) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isUploadMode = data.personImageMode === 'upload';
  // キャラクター (ethnicity='character') は年齢・体格・身体的特徴を持たず、
  // 性別も任意入力 (未指定可) になる (P1-1)。
  const isCharacter = isCharacterActor({ ethnicity: data.ethnicity });

  // 人種セグメント変更。キャラクターへ切り替えた場合は年齢・体格・身体的特徴を
  // クリアする (BE 側でも character 選択時は null 強制されるが、FE 表示も揃える)。
  const handleEthnicityChange = useCallback((ethnicity: string) => {
    if (ethnicity === 'character') {
      onUpdate({ ethnicity, ageMin: null, ageMax: null, bodyType: 'none', physicalFeatures: 'none' });
    } else {
      onUpdate({ ethnicity });
    }
  }, [onUpdate]);

  // クレジットレートを取得（人物生成は2K解像度。モデル別に消費ポイントを算出）
  const { data: creditRatesData } = useCreditRates();
  const findRate = useCallback(
    (resourceType: string): number | null => {
      if (!creditRatesData?.rates) return null;
      const rate = creditRatesData.rates.find(
        (r) => r.service_type === 'image_generation' && r.resource_type === resourceType
      );
      return rate?.credits_per_unit ?? null;
    },
    [creditRatesData]
  );
  const creditByModel = useMemo(
    () => ({
      'gpt-image-2.5-flare-auto': findRate(RESOURCE_TYPE_IMAGE_GPT25_FLARE_AUTO_2K),
      'gemini-3-pro-image-preview': findRate(RESOURCE_TYPE_IMAGE_QUALITY_2K),
    }),
    [findRate]
  );
  const credits = creditByModel[data.model];

  // 画像アップロード処理
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 画像ファイルかチェック
    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください');
      return;
    }

    try {
      // 2K以下にダウンスケール
      const { base64, mimeType } = await downscaleImage(file);

      // アップロード画像は素材 ID を持たない (シート生成・名前提案では base64 を送り、
      // バックエンドが課金先の組織で一時素材化する)
      onUpdate({
        personImageBase64: base64,
        personMimeType: mimeType,
        personMaterialId: null,
        personImageHistory: [{ base64, mimeType, materialId: null }],
        personImageIndex: 0,
      });
      setError(null);
    } catch (err) {
      console.error('Failed to process image:', err);
      setError('画像の処理に失敗しました');
    }
  }, [onUpdate]);

  // アップロードボタンクリック
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // ドラッグ&ドロップ
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください');
      return;
    }

    try {
      // 2K以下にダウンスケール
      const { base64, mimeType } = await downscaleImage(file);

      // アップロード画像は素材 ID を持たない (シート生成・名前提案では base64 を送り、
      // バックエンドが課金先の組織で一時素材化する)
      onUpdate({
        personImageBase64: base64,
        personMimeType: mimeType,
        personMaterialId: null,
        personImageHistory: [{ base64, mimeType, materialId: null }],
        personImageIndex: 0,
      });
      setError(null);
    } catch (err) {
      console.error('Failed to process image:', err);
      setError('画像の処理に失敗しました');
    }
  }, [onUpdate]);

  // 生成処理
  const handleGenerate = useCallback(async () => {
    if (!data.prompt.trim()) {
      setError('プロンプトを入力してください');
      return;
    }

    setIsGenerating(true);
    setError(null);

    // 名前提案は画像に依存しないので、画像生成の完了を待たずに走らせておく。
    // 画像生成 (10〜30秒) の裏で終わるため、Step2 での待ち時間が消える。
    onPrefetchNames?.();

    try {
      // 体格と身体的特徴はラベル（日本語）で送信
      const bodyTypeLabel = data.bodyType && data.bodyType !== 'none'
        ? buildOptions.find((o) => o.key === data.bodyType)?.label
        : undefined;
      const physicalFeaturesLabel = data.physicalFeatures && data.physicalFeatures !== 'none'
        ? appearanceOptions.find((o) => o.key === data.physicalFeatures)?.label
        : undefined;

      const result = await generatePersonApi({
        prompt: data.prompt,
        model: data.model,
        gender: data.gender !== 'none' ? data.gender : undefined,
        age_min: data.ageMin ?? undefined,
        age_max: data.ageMax ?? undefined,
        ethnicity: data.ethnicity,
        body_type: bodyTypeLabel,
        physical_features: physicalFeaturesLabel,
      });

      // 履歴に追加 (素材 ID も保持し、履歴を戻ったときも参照で渡せるようにする)
      const newImage = { base64: result.image_base64, mimeType: result.mime_type, materialId: result.material_id };
      const newHistory = [...data.personImageHistory, newImage];
      const newIndex = newHistory.length - 1;

      onUpdate({
        personImageBase64: result.image_base64,
        personMimeType: result.mime_type,
        personMaterialId: result.material_id,
        personImageHistory: newHistory,
        personImageIndex: newIndex,
      });

      // localStorage 履歴に追加 (DB 側は usage_logs.metadata.user_inputs から導出)
      addToPromptHistory(PROMPT_HISTORY_KEYS.ACTOR_GENERATION, data.prompt);
      setHasGenerated(true);

      // 生成成功時のコールバック
      onGenerateSuccess?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || '生成に失敗しました');
    } finally {
      setIsGenerating(false);
    }
  }, [data, onUpdate, buildOptions, appearanceOptions, generatePersonApi, onPrefetchNames]);

  // 履歴からプロンプトを選択
  const handleSelectHistory = useCallback((prompt: string) => {
    onUpdate({ prompt });
    setShowHistory(false);
  }, [onUpdate]);

  return (
    <div className={styles.stepContainer}>
      {/* モード選択タブ */}
      <div style={{ marginBottom: '20px' }}>
        <SegmentedTabs
          stretch
          ariaLabel="人物画像の入力方法"
          options={[
            { value: 'generate' as const, label: 'AIで人物を生成', iconMaskUrl: '/reward_stars_2.svg' },
            { value: 'upload' as const, label: '画像アップロード', iconMaskUrl: '/export_file.svg' },
          ]}
          value={isUploadMode ? 'upload' : 'generate'}
          onChange={(v) => onUpdate({ personImageMode: v })}
        />
      </div>

      {/* アップロードモード: ドロップゾーン */}
      {isUploadMode && (
        <div
          className={`${styles.dropZone} ${isDragging ? styles.dropZoneDragging : ''} ${data.personImageBase64 ? styles.dropZoneHasImage : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className={styles.fileInput}
          />
          {data.personImageBase64 ? (
            <span className={styles.dropZoneText}>
              画像が選択されています（クリックまたはドロップで変更）
            </span>
          ) : (
            <span className={styles.dropZoneText}>
              クリックまたはドラッグ&ドロップで画像を選択
            </span>
          )}
        </div>
      )}

      {/* 属性セクション見出し（アップロードモードのみ） */}
      {isUploadMode && (
        <h3 className={styles.attributeHeading}>属性を設定してください</h3>
      )}

      <div className={styles.formGrid}>
        {/* 1行目: 人種 (性別と同じ横並びセグメント。キャラクター切替で人間属性を
            無効化するため先頭の行に置く。3ボタンで幅を取るため単独行) */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              人種{isUploadMode && <span className={styles.required}>*</span>}
            </label>
            <div className={styles.optionGroupButtons}>
              {([['jp', '日本人'], ['foreigner', '外国人'], ['character', 'キャラクター']] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`${styles.optionButton} ${data.ethnicity === value ? styles.optionButtonActive : ''}`}
                  onClick={() => handleEthnicityChange(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 2行目: 性別・年齢範囲 */}
        <div className={styles.formRow}>
          {/* 性別 */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              性別 {!isCharacter && <span className={styles.required}>*</span>}
            </label>
            <div className={styles.optionGroupButtons}>
              <button
                type="button"
                className={`${styles.optionButton} ${data.gender === 'male' ? styles.optionButtonActive : ''}`}
                onClick={() => onUpdate({ gender: 'male' })}
              >
                男性
              </button>
              <button
                type="button"
                className={`${styles.optionButton} ${data.gender === 'female' ? styles.optionButtonActive : ''}`}
                onClick={() => onUpdate({ gender: 'female' })}
              >
                女性
              </button>
              {/* キャラクターのみ性別「未指定」を選べる (人間俳優は従来どおり必須) */}
              {isCharacter && (
                <button
                  type="button"
                  className={`${styles.optionButton} ${data.gender === 'none' ? styles.optionButtonActive : ''}`}
                  onClick={() => onUpdate({ gender: 'none' })}
                >
                  未指定
                </button>
              )}
            </div>
          </div>

          {/* 年齢範囲（キャラクターは対象外） */}
          {!isCharacter && (
            <div className={styles.formGroup}>
              <label className={styles.label}>
                年齢範囲{isUploadMode && <span className={styles.required}>*</span>}
              </label>
              <div className={styles.ageRange}>
                <input
                  type="number"
                  value={data.ageMin ?? ''}
                  onChange={(e) => onUpdate({ ageMin: e.target.value ? parseInt(e.target.value, 10) : null })}
                  placeholder="20"
                  min="0"
                  max="100"
                  className={styles.inputSmall}
                />
                <span>〜</span>
                <input
                  type="number"
                  value={data.ageMax ?? ''}
                  onChange={(e) => onUpdate({ ageMax: e.target.value ? parseInt(e.target.value, 10) : null })}
                  placeholder="40"
                  min="0"
                  max="100"
                  className={styles.inputSmall}
                />
                <span>歳</span>
              </div>
            </div>
          )}
        </div>

        {/* 3行目: 体格・身体的特徴 (AI生成モードのみ) */}
        {!isUploadMode && !isCharacter && (
        <div className={styles.formRow}>
          {/* 体格（AI生成モードのみ・キャラクターは対象外） */}
          {!isUploadMode && !isCharacter && (
            <div className={styles.formGroup}>
              <label className={styles.label}>体格</label>
              <select
                value={data.bodyType}
                onChange={(e) => onUpdate({ bodyType: e.target.value })}
                className={styles.select}
              >
                <option value="none">指定なし</option>
                {buildOptions
                  .filter((opt) => opt.key !== 'none')
                  .map((opt) => (
                    <option key={opt.id} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* 身体的特徴（AI生成モードのみ・キャラクターは対象外） */}
          {!isUploadMode && !isCharacter && (
            <div className={styles.formGroup}>
              <label className={styles.label}>身体的特徴</label>
              <select
                value={data.physicalFeatures}
                onChange={(e) => onUpdate({ physicalFeatures: e.target.value })}
                className={styles.select}
              >
                <option value="none">指定なし</option>
                {appearanceOptions
                  .filter((opt) => opt.key !== 'none')
                  .map((opt) => (
                    <option key={opt.id} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>
        )}
      </div>

      {/* AI生成モード: プロンプト */}
      {!isUploadMode && (
        <div className={styles.formGroupFull}>
          <div className={styles.labelRow}>
            <label className={styles.label}>プロンプト</label>
            <button
              type="button"
              className="gen-text-link"
              onClick={() => setShowHistory(!showHistory)}
            >
              <span aria-hidden="true" style={{ display: 'inline-block', width: 12, height: 12, marginRight: 4, verticalAlign: '-2px', backgroundColor: 'currentColor', WebkitMaskImage: 'url(/time_clock_circle.svg)', maskImage: 'url(/time_clock_circle.svg)', WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat', WebkitMaskPosition: 'center', maskPosition: 'center' }} />
              履歴
            </button>
          </div>
          <textarea
            value={data.prompt}
            onChange={(e) => onUpdate({ prompt: e.target.value })}
            placeholder="丸の内の大手企業に勤務するエリート男性。高級スーツを着て..."
            className={styles.textarea}
            rows={4}
          />

          {/* プロンプト履歴 (全画面モーダル。localStorage + DB 履歴のマージ表示) */}
          <PromptHistoryModal
            isOpen={showHistory}
            onClose={() => setShowHistory(false)}
            scope="actor_generation"
            storageKey={PROMPT_HISTORY_KEYS.ACTOR_GENERATION}
            onSelect={handleSelectHistory}
            title="プロンプトの履歴"
          />
        </div>
      )}

      {/* エラー表示 */}
      {error && <div className={styles.error}>{error}</div>}

      {/* 生成ボタン（AI生成モードのみ）。モデル選択はボタンの ▼ ドロップダウン */}
      {!isUploadMode && (
        <div className={styles.generateButtonContainer}>
          <ActorGenerateButton
            label={hasGenerated ? 'リトライ' : '生成'}
            selectedCredits={credits}
            isGenerating={isGenerating}
            disabled={!data.prompt.trim() || (!isCharacter && data.gender === 'none')}
            onGenerate={handleGenerate}
            model={data.model}
            onModelChange={(model) => onUpdate({ model })}
            creditByModel={creditByModel}
            dropdownId="actor-person-generate"
          />
        </div>
      )}

    </div>
  );
}
